import { Badge, Card } from "@/components/ui";
import type { HomeFeedLogItem } from "@/types";
import { compactTime } from "@/utils/format";
import { cn } from "@/lib/utils";
import { feedTypeLabel, isDerived } from "@/utils/engagement";
import { HOME_FEED_TABLE_COLUMNS } from "./homeFeedTable";

/**
 * One reaction label from the event timestamps.
 *
 * `state` is the backend's lifecycle value and the events are what the client
 * reported; they agree in practice, but the events are the primary source
 * here because they carry the timing the operator is judging.
 */
function reaction(item: HomeFeedLogItem) {
  if (item.events.sent_at) return { label: "전환", accent: true };
  if (item.events.dismissed_at) return { label: "무시", accent: false };
  if (item.events.opened_at) return { label: "열람", accent: false };
  if (item.events.impression_at) return { label: "노출만", accent: false };
  return { label: "미관측", accent: false };
}

export function HomeFeedRow({
  item,
  selected,
  onSelect,
}: {
  item: HomeFeedLogItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const state = reaction(item);
  const conversion = item.conversion;

  return (
    <Card className={cn("overflow-hidden", selected && "border-primary bg-primary/8")}>
      <button
        type="button"
        onClick={onSelect}
        className={`${HOME_FEED_TABLE_COLUMNS} min-h-16 w-full cursor-pointer border-0 bg-transparent px-4.5 py-3.5 text-left text-inherit hover:bg-accent-hover`}
      >
        <span className="text-muted-foreground">{compactTime(item.generated_at)}</span>
        <Badge size="compact">{feedTypeLabel(item.type)}</Badge>
        <span className="min-w-0 truncate">{item.email}</span>
        <span className="min-w-0 truncate text-muted-foreground">
          {item.session.display_name || item.session.session_id || "-"}
        </span>
        <span className="min-w-0 truncate text-muted-foreground">
          {(item.text || item.title || "").trim() || "(내용 없음)"}
        </span>
        <span className="text-muted-foreground">
          {item.events.impression_at ? "O" : "-"}
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <Badge size="compact" variant={state.accent ? "accent" : "default"}>
            {state.label}
          </Badge>
          {/* Only when the conversion message was matched by time window. */}
          {conversion && isDerived(conversion.source) && (
            <Badge
              size="compact"
              title="전송 이벤트에 메시지 ID가 없어 시간 기반으로 추정한 연결입니다."
            >
              추정
            </Badge>
          )}
        </span>
        <span className="text-muted-foreground">
          {conversion ? `${conversion.turn_count}턴` : "-"}
        </span>
        <Badge size="compact">{item._environment}</Badge>
      </button>
    </Card>
  );
}
